import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TextUtils } from '../../../../../../shared/utils/text-utils';

// Range preset keys for internal logic
export enum RangePresetKey {
    TODAY = 'TODAY',
    YESTERDAY_TODAY = 'YESTERDAY_TODAY',
    YESTERDAY = 'YESTERDAY',
    LAST_7_DAYS = 'LAST_7_DAYS',
    LAST_30_DAYS = 'LAST_30_DAYS',
    THIS_MONTH = 'THIS_MONTH',
    PREVIOUS_MONTH = 'PREVIOUS_MONTH',
    PREVIOUS_YEAR = 'PREVIOUS_YEAR',
    CUSTOM = 'CUSTOM'
}

@Component({
    selector: 'app-custom-range-panel',
    templateUrl: './custom-range-panel.component.html',
    styleUrls: ['./custom-range-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CustomRangePanelComponent<D> {
    // list of range presets we want to provide:
    @Input() customPresets: RangePresetKey[] = [
        RangePresetKey.TODAY,
        RangePresetKey.YESTERDAY_TODAY,
        RangePresetKey.YESTERDAY,
        RangePresetKey.LAST_7_DAYS,
        RangePresetKey.LAST_30_DAYS,
        RangePresetKey.THIS_MONTH,
        RangePresetKey.PREVIOUS_MONTH,
        RangePresetKey.PREVIOUS_YEAR
    ];
    @HostBinding('class.touch-ui')
    readonly isTouchUi = this.picker.touchUi;
    selectedRange: RangePresetKey | '' = '';
    websiteId: string;
    activeRange: RangePresetKey = RangePresetKey.LAST_7_DAYS;
    activeSwitcher = 'period';
    private readonly destroy$ = new Subject<void>();

    constructor(
        private dateAdapter: DateAdapter<D>,
        private picker: MatDateRangePicker<D>,
        private calendar: MatCalendar<D>,
        cdr: ChangeDetectorRef
    ) {
        calendar.stateChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            cdr.markForCheck();
            this.loadSelectedRange();
        });
    }

    ngOnInit() {
      this.websiteId = (window.location.pathname).match(/\d+/)[0];
      const periodNumber = localStorage.getItem('dbEcommercePeriodNumber' + this.websiteId);
      if (periodNumber) {
        this.switchPeriod(periodNumber);
      }
      const savedRangeName = localStorage.getItem('dbEcommerceRangeName' + this.websiteId);
      if (savedRangeName) {
        // Check if it's already an enum key or needs migration
        if (Object.values(RangePresetKey).includes(savedRangeName as RangePresetKey)) {
          this.activeRange = savedRangeName as RangePresetKey;
        } else {
          // Migrate old string value to enum key
          this.activeRange = this.migrateOldRangeName(savedRangeName);
        }
        this.selectRange(this.activeRange);
      }
      this.loadSelectedRange();
    }

    loadSelectedRange() {
        this.selectedRange = '';
        for (const preset of this.customPresets) {
            if (this.isSelectedRange(preset)) {
                this.selectedRange = preset;
                break;
            }
        }
    }

    // Get translated label for a range preset
    getPresetLabel(presetKey: RangePresetKey): string {
        switch (presetKey) {
            case RangePresetKey.TODAY:
                return TextUtils.getText('text_today');
            case RangePresetKey.YESTERDAY_TODAY:
                return TextUtils.getText('text_yesterdayToday');
            case RangePresetKey.YESTERDAY:
                return TextUtils.getText('text_yesterday');
            case RangePresetKey.LAST_7_DAYS:
                return TextUtils.getText('text_last7days');
            case RangePresetKey.LAST_30_DAYS:
                return TextUtils.getText('text_last30Days');
            case RangePresetKey.THIS_MONTH:
                return TextUtils.getText('text_thisMonth');
            case RangePresetKey.PREVIOUS_MONTH:
                return TextUtils.getText('text_previousMonth');
            case RangePresetKey.PREVIOUS_YEAR:
                return TextUtils.getText('text_previousYear');
            case RangePresetKey.CUSTOM:
                return TextUtils.getText('text_custom');
            default:
                return presetKey;
        }
    }

    // called when user selects a range preset:
    selectRange(rangeName: RangePresetKey): void {
        this.activeRange = rangeName;
        localStorage.setItem('dbEcommerceRangeName' + this.websiteId, rangeName);
        if (rangeName === RangePresetKey.CUSTOM) return;
        const [start, end] = this.calculateDateRange(rangeName);
        (this.calendar.selected as any).start = start;
        (this.calendar.selected as any).end = end;
        this.calendar.updateTodaysDate();
        this.calendar.activeDate = start;
        this.calendar.activeDate = end;
        // this.picker.select(start);
        // this.picker.select(end);
        // this.picker.close();
    }

    private calculateDateRange(rangeName: RangePresetKey): [start: D, end: D] {
        const today = this.today;

        switch (rangeName) {
            case RangePresetKey.TODAY:
                return [today, today];

            case RangePresetKey.YESTERDAY_TODAY: {
                const start = this.dateAdapter.addCalendarDays(today, -1);
                return [start, today];
            }
            case RangePresetKey.YESTERDAY: {
                const start = this.dateAdapter.addCalendarDays(today, -1);
                return [start, start];
            }
            case RangePresetKey.LAST_7_DAYS: {
                const start = this.dateAdapter.addCalendarDays(today, -6);
                return [start, today];
            }
            case RangePresetKey.LAST_30_DAYS: {
                const start = this.dateAdapter.addCalendarDays(today, -29);
                return [start, today];
            }
            case RangePresetKey.THIS_MONTH: {
                return this.calculateMonth(today);
            }
            case RangePresetKey.PREVIOUS_MONTH: {
                const thisDayLastMonth = this.dateAdapter.addCalendarMonths(today, -1);
                return this.calculateMonth(thisDayLastMonth);
            }
            case RangePresetKey.PREVIOUS_YEAR: {
                const thisDayLastMonth = this.dateAdapter.addCalendarYears(today, -1);
                return this.calculateMonth(thisDayLastMonth);
            }
        }
    }

    private calculateMonth(forDay: D): [start: D, end: D] {
        const year = this.dateAdapter.getYear(forDay);
        const month = this.dateAdapter.getMonth(forDay);
        const start = this.dateAdapter.createDate(year, month, 1);
        const end = this.dateAdapter.addCalendarDays(
            start,
            this.dateAdapter.getNumDaysInMonth(forDay) - 1
        );
        return [start, end];
    }

    private get today(): D {
        const today = this.dateAdapter.today(); // .getValidDateOrNull(new Date());
        if (today === null) {
            throw new Error('date creation failed');
        }
        return today;
    }
    get selectedDateRange() {
        const selectedRange: any = this.calendar.selected;
        let displayText = '';
        if (selectedRange.start !== null)
            displayText += new Date(String(selectedRange.start)).toLocaleDateString();
        if (selectedRange.end !== null)
            displayText +=
                ' - ' + new Date(String(selectedRange.end)).toLocaleDateString();
        return displayText;
    }

    ngOnDestroy() {
        this.destroy$.next(); // will trigger unsubscription in takeUntil
    }

    isSelectedRange(rangeName: RangePresetKey) {
        const [start, end] = this.calculateDateRange(rangeName);
        const selectedRange: any = this.calendar.selected;
        return (
            selectedRange.start !== null &&
            selectedRange.end !== null &&
            new Date(String(selectedRange.start)).toDateString() ===
            new Date(String(start)).toDateString() &&
            new Date(String(selectedRange.end)).toDateString() ===
            new Date(String(end)).toDateString()
        );
    }

    // Helper to migrate old string-based localStorage values to enum keys
    private migrateOldRangeName(oldName: string): RangePresetKey {
        const migrationMap: { [key: string]: RangePresetKey } = {
            'Today': RangePresetKey.TODAY,
            'Yesterday & today': RangePresetKey.YESTERDAY_TODAY,
            'Yesterday': RangePresetKey.YESTERDAY,
            'Last 7 days': RangePresetKey.LAST_7_DAYS,
            'Last 30 days': RangePresetKey.LAST_30_DAYS,
            'This month': RangePresetKey.THIS_MONTH,
            'Previous month': RangePresetKey.PREVIOUS_MONTH,
            'Previous year': RangePresetKey.PREVIOUS_YEAR,
            'Custom': RangePresetKey.CUSTOM
        };
        return migrationMap[oldName] || RangePresetKey.LAST_7_DAYS;
    }

    public switchPeriod(value) {
        localStorage.setItem('dbEcommercePeriodNumber' + this.websiteId, value);
        this.activeSwitcher = value;
    }
}
